import React, { useState, useEffect } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { POC, Vendor } from '../../types';
import { guessTimezone } from '../../utils/timezone';

interface PocModalProps {
  active: boolean;
  onClose: () => void;
  onSave: (data: Partial<POC>) => void;
  poc?: POC | null;
  vendors: Vendor[];
  /** When set, the POC is being added from within a specific vendor: vendor type and vendor
   *  are pre-filled from this vendor and shown read-only instead of as selectable dropdowns. */
  lockedVendor?: Vendor | null;
}

const emptyPoc: Partial<POC> = {
  poc_name: '', poc_position: '', vendor_type: '', vendor: '',
  office_phone: '', cell_number: '', email: '', working_hours: '',
};

const DEFAULT_DAYS: { [key: string]: boolean } = { su: false, m: false, t: false, w: false, th: false, f: false, sa: false };
const DAY_LABEL_TO_KEY: { [label: string]: string } = { Su: 'su', M: 'm', T: 't', W: 'w', Th: 'th', F: 'f', Sa: 'sa' };

interface ParsedWorkingHours {
  days: { [key: string]: boolean };
  startHH: string;
  startMM: string;
  endHH: string;
  endMM: string;
}

/** Parse a stored working-hours string (e.g. "M, T, W 08:00-17:00 America/New_York") back into
 *  the day checkboxes and time selects so an edited POC keeps its hours instead of clearing them. */
const parseWorkingHours = (workingHours: string): ParsedWorkingHours => {
  const fallback: ParsedWorkingHours = { days: { ...DEFAULT_DAYS }, startHH: '08', startMM: '00', endHH: '17', endMM: '00' };
  if (!workingHours) return fallback;
  const timeMatch = workingHours.match(/(\d{2}):(\d{2})-(\d{2}):(\d{2})/);
  const dayPart = timeMatch ? workingHours.slice(0, workingHours.indexOf(timeMatch[0])) : workingHours;
  const days = { ...DEFAULT_DAYS };
  dayPart.split(',').map((token) => token.trim()).filter(Boolean).forEach((token) => {
    const key = DAY_LABEL_TO_KEY[token];
    if (key) days[key] = true;
  });
  return {
    days,
    startHH: timeMatch ? timeMatch[1] : '08',
    startMM: timeMatch ? timeMatch[2] : '00',
    endHH: timeMatch ? timeMatch[3] : '17',
    endMM: timeMatch ? timeMatch[4] : '00',
  };
};

const PocModal: React.FC<PocModalProps> = ({ active, onClose, onSave, poc, vendors, lockedVendor }) => {
  const [form, setForm] = useState<Partial<POC>>(emptyPoc);
  const [days, setDays] = useState<{ [key: string]: boolean }>({ su: false, m: false, t: false, w: false, th: false, f: false, sa: false });
  const [startHH, setStartHH] = useState('08');
  const [startMM, setStartMM] = useState('00');
  const [endHH, setEndHH] = useState('17');
  const [endMM, setEndMM] = useState('00');

  useEffect(() => {
    if (poc) {
      setForm(poc);
      const parsed = parseWorkingHours(poc.working_hours || '');
      setDays(parsed.days);
      setStartHH(parsed.startHH); setStartMM(parsed.startMM);
      setEndHH(parsed.endHH); setEndMM(parsed.endMM);
    } else {
      setForm(lockedVendor
        ? { ...emptyPoc, vendor_type: lockedVendor.vendor_type, vendor: lockedVendor.vendor_name }
        : emptyPoc);
      setDays({ ...DEFAULT_DAYS });
      setStartHH('08'); setStartMM('00'); setEndHH('17'); setEndMM('00');
    }
  }, [poc, active, lockedVendor]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const filteredVendors = vendors.filter((v) => v.vendor_type === form.vendor_type);
  const selectedVendor = lockedVendor || vendors.find((v) => v.vendor_name === form.vendor);
  const timezone = guessTimezone(selectedVendor);

  const handleWeekdays = (checked: boolean) => {
    setDays((prev) => ({ ...prev, m: checked, t: checked, w: checked, th: checked, f: checked }));
  };

  const allWeekdaysChecked = days.m && days.t && days.w && days.th && days.f;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dayNames = Object.entries(days).filter(([, v]) => v).map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
    const tz = guessTimezone(selectedVendor);
    const wh = dayNames.length > 0 ? `${dayNames.join(', ')} ${startHH}:${startMM}-${endHH}:${endMM}${tz ? ' ' + tz : ''}` : '';
    onSave({ ...form, working_hours: wh });
  };

  const hhOptions = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const mmOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal modal-poc">
        <div className="modal-header">
          <h2>{poc ? 'Edit POC' : 'New POC'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="poc-fields">
            <div className="poc-pair-row">
              <div className="form-group">
                <label>POC Name</label>
                <input type="text" value={form.poc_name || ''} onChange={(e) => handleChange('poc_name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>POC Position</label>
                <input type="text" value={form.poc_position || ''} onChange={(e) => handleChange('poc_position', e.target.value)} />
              </div>
            </div>
            <div className="poc-pair-row">
              <div className="form-group">
                <label>Vendor Type</label>
                {lockedVendor ? (
                  <input type="text" value={form.vendor_type || ''} readOnly tabIndex={-1} />
                ) : (
                  <select value={form.vendor_type || ''} onChange={(e) => { handleChange('vendor_type', e.target.value); handleChange('vendor', ''); }} required>
                    <option value="">Select…</option>
                    <option value="Component Maintenance">Component Maintenance</option>
                    <option value="145-MRO">145-MRO</option>
                  </select>
                )}
              </div>
              <div className="form-group">
                <label>Vendor</label>
                {lockedVendor ? (
                  <input type="text" value={form.vendor || ''} readOnly tabIndex={-1} />
                ) : (
                  <select value={form.vendor || ''} onChange={(e) => handleChange('vendor', e.target.value)} required>
                    <option value="">Select vendor type first…</option>
                    {filteredVendors.map((v) => (
                      <option key={v.id} value={v.vendor_name}>{v.vendor_name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {selectedVendor && (
              <div className="poc-vendor-info">
                <div className="form-group">
                  <label>Location Address</label>
                  <input type="text" value={selectedVendor.loc_address} readOnly tabIndex={-1} />
                </div>
                <div className="poc-pair-row">
                  <div className="form-group">
                    <label>Location City</label>
                    <input type="text" value={selectedVendor.loc_city} readOnly tabIndex={-1} />
                  </div>
                  <div className="form-group">
                    <label>Location State / Province</label>
                    <input type="text" value={selectedVendor.loc_state} readOnly tabIndex={-1} />
                  </div>
                </div>
                <div className="poc-pair-row">
                  <div className="form-group">
                    <label>Location Country</label>
                    <input type="text" value={selectedVendor.loc_country} readOnly tabIndex={-1} />
                  </div>
                  <div className="form-group">
                    <label>Location Zip Code</label>
                    <input type="text" value={selectedVendor.loc_zip} readOnly tabIndex={-1} />
                  </div>
                </div>
              </div>
            )}

            <div className="poc-phone-row">
              <div className="form-group poc-centered-label">
                <label>Office Phone Number</label>
                <input type="tel" value={form.office_phone || ''} onChange={(e) => handleChange('office_phone', e.target.value)} />
              </div>
              <div className="form-group poc-centered-label">
                <label>Cell Number</label>
                <input type="tel" value={form.cell_number || ''} onChange={(e) => handleChange('cell_number', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email || ''} onChange={(e) => handleChange('email', e.target.value)} placeholder="e.g. name@company.com" />
            </div>

            <div className="form-group poc-centered-label">
              <label>Working Hours</label>
              <div className="working-hours-section">
                <div className="wh-main-row">
                  <div className="wh-days-row">
                    {[['su', 'Su'], ['m', 'M'], ['t', 'T'], ['w', 'W'], ['th', 'Th'], ['f', 'F'], ['sa', 'Sa']].map(([key, label]) => (
                      <label key={key} className="wh-day">
                        <span>{label}</span>
                        <input type="checkbox" checked={days[key]} onChange={(e) => setDays((prev) => ({ ...prev, [key]: e.target.checked }))} />
                      </label>
                    ))}
                    <label className="wh-day wh-weekdays">
                      <span>Weekdays</span>
                      <input type="checkbox" checked={allWeekdaysChecked} onChange={(e) => handleWeekdays(e.target.checked)} />
                    </label>
                  </div>
                  <div className="wh-times-row">
                    <div className="wh-time-group poc-centered-label">
                      <label className="wh-time-label">Start Time</label>
                      <div className="wh-time-selects">
                        <select value={startHH} onChange={(e) => setStartHH(e.target.value)}>
                          {hhOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <span className="wh-colon">:</span>
                        <select value={startMM} onChange={(e) => setStartMM(e.target.value)}>
                          {mmOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="wh-time-group poc-centered-label">
                      <label className="wh-time-label">End Time</label>
                      <div className="wh-time-selects">
                        <select value={endHH} onChange={(e) => setEndHH(e.target.value)}>
                          {hhOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <span className="wh-colon">:</span>
                        <select value={endMM} onChange={(e) => setEndMM(e.target.value)}>
                          {mmOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="wh-time-group poc-centered-label">
                      <label className="wh-time-label">Time Zone</label>
                      <input type="text" className="wh-tz-input" value={timezone} readOnly tabIndex={-1} placeholder="Auto" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save POC</button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
};

export default PocModal;
